import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import html2canvas from "html2canvas";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Pill, Callout, Avatar } from "@/components/brand";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  GraduationCap, Library, Sparkles, TrendingUp, PlayCircle, Download, CheckCircle2,
  ArrowLeft, ExternalLink, Award, BookOpen, FileText, AlertCircle, Link as LinkIcon,
  ChevronDown, ChevronUp,
} from "lucide-react";

const TABS = [
  { id: "onboarding", label: "Lessons", icon: GraduationCap },
  { id: "library", label: "Resource Library", icon: Library },
  { id: "ask", label: "Ask the Classroom", icon: Sparkles },
  { id: "glossary", label: "Glossary", icon: BookOpen },
  { id: "progress", label: "Progress", icon: TrendingUp },
];

const COURSES = {
  trademark: {
    endpoint: "/classroom",
    title: "The Trademark Classroom",
    subtitle: "Your deep fulfillment training — the guided onboarding path plus an evergreen resource library for cross-training.",
    tabs: ["onboarding", "library", "ask", "glossary", "progress"],
    onboardingLabel: "Lessons",
  },
  orientation: {
    endpoint: "/orientation",
    title: "New Teammate Orientation",
    subtitle: "Every new teammate's warm welcome to Indie Law — who we are, what we value, and how we work together.",
    tabs: ["onboarding", "progress"],
    onboardingLabel: "Orientation",
  },
};

export default function Classroom({ mode = "trademark" }) {
  const cfg = COURSES[mode] || COURSES.trademark;
  const [data, setData] = useState(null);
  const [progress, setProgress] = useState(null);
  const [tab, setTab] = useState("onboarding");
  const [lesson, setLesson] = useState(null);
  const [glossary, setGlossary] = useState([]);

  const loadProgress = async () => setProgress((await api.get("/classroom/progress")).data);
  useEffect(() => {
    setData(null); setLesson(null); setTab("onboarding");
    api.get(cfg.endpoint).then(({ data }) => setData(data));
    api.get("/glossary").then(({ data }) => setGlossary(data)).catch(() => {});
    loadProgress();
  }, [cfg.endpoint]);

  if (!data) return <div className="p-10 text-[#141B4D] animate-pulse">Loading…</div>;

  const completedIds = new Set((progress?.mine || []).filter((p) => p.completed).map((p) => p.lesson_id));
  const courseLessonIds = [
    ...data.onboarding.chapters.filter((ch) => ch.counts !== false).flatMap((ch) => ch.lessons.map((l) => l.id)),
    ...(data.resource_library?.pods || []).flatMap((p) => p.lessons.map((l) => l.id)),
  ];
  const shownTabs = TABS.filter((t) => cfg.tabs.includes(t.id));

  const flat = [];
  data.onboarding.chapters.forEach((ch, ci) =>
    ch.lessons.forEach((l, li) => flat.push({ lesson: l, label: `${ci}.${li + 1}`, chapterTitle: ch.title })));
  const curIdx = lesson ? flat.findIndex((f) => f.lesson.id === lesson.id) : -1;
  const cur = curIdx >= 0 ? flat[curIdx] : null;
  const prev = curIdx > 0 ? flat[curIdx - 1] : null;
  const next = curIdx >= 0 && curIdx < flat.length - 1 ? flat[curIdx + 1] : null;
  const goTo = (l) => { setLesson(l); window.scrollTo({ top: 0, behavior: "smooth" }); };

  if (lesson) return (
    <LessonView lesson={lesson} label={cur?.label} chapterTitle={cur?.chapterTitle}
      prev={prev} next={next} onNav={goTo} glossary={glossary}
      onBack={() => setLesson(null)} onComplete={() => { loadProgress(); }} />
  );
  return (
    <div className="p-8 md:p-10">
      <PageHeader title={cfg.title} subtitle={cfg.subtitle} />

      <div className="flex flex-wrap gap-2 mb-6 border-b border-[#BDC5DB]/60">
        {shownTabs.map((t) => (
          <button key={t.id} data-testid={`classroom-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-t-lg -mb-px border-b-2 transition-all ${
              tab === t.id ? "border-[#FFD100] text-[#141B4D]" : "border-transparent text-[#101820]/50 hover:text-[#141B4D]"}`}>
            <t.icon className="w-4 h-4" /> {t.id === "onboarding" ? cfg.onboardingLabel : t.label}
          </button>
        ))}
      </div>

      {tab === "onboarding" && <Onboarding section={data.onboarding} completedIds={completedIds} onOpen={setLesson} />}
      {tab === "library" && <ResourceLibrary section={data.resource_library} completedIds={completedIds} onOpen={setLesson} />}
      {tab === "ask" && <Ask />}
      {tab === "glossary" && <GlossaryView glossary={glossary} />}
      {tab === "progress" && <ProgressView progress={progress} mode={mode} courseTitle={cfg.title} lessonIds={courseLessonIds} />}
    </div>
  );
}

function GlossaryTerm({ term, def, children }) {
  return (
    <span className="relative group cursor-help border-b border-dotted border-[#1D57A5]/70 text-[#1D57A5] font-semibold">
      {children}
      <span aria-hidden="true" className="pointer-events-none absolute left-0 bottom-full mb-2 z-40 w-64 rounded-lg bg-[#141B4D] text-white text-xs leading-snug p-3 opacity-0 group-hover:opacity-100 transition-opacity shadow-xl">
        <span className="block font-bold text-[#FFD100] mb-1">{term}</span>{def}
      </span>
    </span>
  );
}

function glossaryize(text, glossary) {
  if (!text || !glossary?.length) return text;
  const terms = [...glossary].sort((a, b) => b.term.length - a.term.length);
  const escaped = terms.map((g) => g.term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`\\b(${escaped.join("|")})\\b`, "i");
  const used = new Set();
  const out = [];
  let rest = String(text);
  let key = 0;
  while (rest.length) {
    const m = re.exec(rest);
    if (!m) { out.push(rest); break; }
    out.push(rest.slice(0, m.index));
    const matched = m[0];
    const lower = matched.toLowerCase();
    const g = terms.find((x) => x.term.toLowerCase() === lower);
    if (g && !used.has(lower)) {
      used.add(lower);
      out.push(<GlossaryTerm key={key++} term={g.term} def={g.definition}>{matched}</GlossaryTerm>);
    } else {
      out.push(matched);
    }
    rest = rest.slice(m.index + matched.length);
  }
  return out;
}

function SpectrumInteractive({ spectrum }) {
  const [sel, setSel] = useState(spectrum.levels.length - 1);
  const active = spectrum.levels[sel];
  return (
    <div className="mt-4 bg-white rounded-xl p-5 shadow-sm border border-black/5" data-testid="distinctiveness-spectrum">
      <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-1">{spectrum.title}</div>
      {spectrum.subtitle && <p className="text-sm text-[#101820]/70 mb-4">{spectrum.subtitle}</p>}
      <div className="flex rounded-lg overflow-hidden border border-[#E4E1D8]">
        {spectrum.levels.map((lv, i) => (
          <button key={i} data-testid={`spectrum-level-${i}`} onClick={() => setSel(i)}
            className="flex-1 py-3 px-1 text-xs sm:text-sm font-bold text-white transition-all relative"
            style={{ background: lv.color, opacity: i === sel ? 1 : 0.55, transform: i === sel ? "scaleY(1.08)" : "none" }}>
            {lv.name}
          </button>
        ))}
      </div>
      <div className="flex justify-between text-[11px] font-semibold uppercase tracking-wide text-[#101820]/45 mt-1.5">
        <span>{spectrum.low}</span><span>{spectrum.high}</span>
      </div>
      <div className="mt-4 rounded-lg p-4" style={{ background: active.color + "14", borderLeft: `4px solid ${active.color}` }} data-testid="spectrum-detail">
        <div className="font-bold text-[#141B4D] flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ background: active.color }} /> {active.name}
        </div>
        <p className="text-sm text-[#101820]/85 mt-1.5">{active.desc}</p>
        <p className="text-sm text-[#101820]/70 mt-2"><span className="font-semibold">Example:</span> {active.example}</p>
      </div>
    </div>
  );
}

function GlossaryView({ glossary }) {
  const [q, setQ] = useState("");
  const list = glossary.filter((g) =>
    !q || g.term.toLowerCase().includes(q.toLowerCase()) || g.definition.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <Callout title="Trademark Glossary">Plain-English definitions from <em>Legally Legit</em>. These terms also show as hover-tooltips inside lessons.</Callout>
      <div className="mt-4 mb-5 max-w-md">
        <Input data-testid="glossary-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search terms…" className="h-11" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3" data-testid="glossary-list">
        {list.map((g, i) => (
          <div key={i} data-testid={`glossary-item-${i}`} className="bg-white rounded-xl p-4 shadow-sm border border-black/5 card-lift">
            <div className="font-bold text-[#141B4D]">{g.term}</div>
            <p className="text-sm text-[#101820]/80 mt-1 leading-relaxed">{g.definition}</p>
          </div>
        ))}
        {list.length === 0 && <div className="text-sm text-[#101820]/50 py-6">No terms match “{q}”.</div>}
      </div>
    </div>
  );
}


const LINK_KIND = {
  sop: { color: "#006747", bg: "#E3F0EA", border: "#006747" },
  uspto: { color: "#1D57A5", bg: "#E1ECF7", border: "#1D57A5" },
  tool: { color: "#B45309", bg: "#FBF3D0", border: "#E6A700" },
  template: { color: "#5B21B6", bg: "#EDE7F9", border: "#8B5CF6" },
  default: { color: "#1D57A5", bg: "#E1ECF7", border: "#1D57A5" },
};

const FRESH = {
  fresh: { label: "Fresh", tone: "green", dot: "#006747" },
  review_due: { label: "Review due", tone: "yellow", dot: "#B45309" },
  stale: { label: "Stale", tone: "red", dot: "#B91C1C" },
  unknown: { label: "Not reviewed", tone: "pale", dot: "#BDC5DB" },
};

function FreshnessBadge({ freshness, uspto }) {
  const f = FRESH[freshness?.status] || FRESH.unknown;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-bold"
        style={{ background: f.tone === "green" ? "#E3F0EA" : f.tone === "yellow" ? "#FBF3D0" : f.tone === "red" ? "#FBE0E0" : "#BDC5DB40", color: f.dot }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: f.dot }} /> {f.label}
      </span>
      {uspto && <Pill tone="cobalt">USPTO-dependent</Pill>}
    </span>
  );
}

function renderInline(text, keyBase) {
  // Bold a leading "Label:" or "Label —", then apply inline **bold**.
  let prefix = null, rest = text;
  const colon = text.match(/^([^:]{1,46}):\s([\s\S]+)$/);
  const dash = text.match(/^([^—]{1,46})\s—\s([\s\S]+)$/);
  if (colon) { prefix = colon[1] + ":"; rest = colon[2]; }
  else if (dash) { prefix = dash[1] + " —"; rest = dash[2]; }
  const nodes = [];
  if (prefix) nodes.push(<strong key={`${keyBase}-p`} className="font-semibold text-[#141B4D]">{prefix} </strong>);
  rest.split(/(\*\*[^*]+\*\*)/g).forEach((p, i) => {
    if (!p) return;
    if (p.startsWith("**") && p.endsWith("**")) nodes.push(<strong key={`${keyBase}-b${i}`} className="font-semibold text-[#141B4D]">{p.slice(2, -2)}</strong>);
    else nodes.push(p);
  });
  return nodes;
}

function RichText({ text, className = "" }) {
  const blocks = [];
  let list = null;
  const flush = () => { if (list) { blocks.push({ type: "ul", items: list }); list = null; } };
  text.split("\n").forEach((raw) => {
    const line = raw.trim();
    if (!line) { flush(); return; }
    if (line.startsWith("- ")) { (list = list || []).push(line.slice(2)); return; }
    flush();
    const letters = line.replace(/[^A-Za-z]/g, "");
    if (letters.length > 2 && line === line.toUpperCase()) { blocks.push({ type: "h", text: line }); return; }
    blocks.push({ type: "p", text: line });
  });
  flush();
  return (
    <div className={`space-y-3 ${className}`}>
      {blocks.map((b, i) => {
        if (b.type === "h") return <div key={i} className="text-[13px] font-bold uppercase tracking-wide text-[#141B4D] pt-2">{b.text}</div>;
        if (b.type === "ul") return (
          <ul key={i} className="space-y-2">
            {b.items.map((it, j) => (
              <li key={j} className="flex gap-2.5">
                <span className="text-[#FFD100] font-bold leading-relaxed">•</span>
                <span className="flex-1">{renderInline(it, `${i}-${j}`)}</span>
              </li>
            ))}
          </ul>
        );
        return <p key={i} className="leading-relaxed">{renderInline(b.text, i)}</p>;
      })}
    </div>
  );
}

function LessonRow({ lesson, done, onOpen, label }) {
  const DurIcon = lesson.video ? PlayCircle : FileText;
  return (
    <button data-testid={`lesson-${lesson.id}`} onClick={() => onOpen(lesson)}
      className="w-full text-left bg-white rounded-xl p-4 shadow-sm border border-black/5 card-lift flex items-center gap-4">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${done ? "bg-[#006747] text-white" : "bg-[#F2E9DB] text-[#141B4D] border border-[#BDC5DB]"}`}>
        {done ? <CheckCircle2 className="w-5 h-5" /> : label}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[#141B4D]">{lesson.title}</div>
        <div className="text-xs text-[#101820]/50 flex items-center gap-2 mt-1 flex-wrap">
          <span className="flex items-center gap-1"><DurIcon className="w-3.5 h-3.5" /> {lesson.duration}</span>
          <span>· {lesson.quiz?.length || 0} quiz Qs</span>
          {lesson.freshness && <FreshnessBadge freshness={lesson.freshness} uspto={lesson.uspto_dependent} />}
        </div>
      </div>
      {done && <Pill tone="green">Completed</Pill>}
    </button>
  );
}

function Onboarding({ section, completedIds, onOpen }) {
  const [showCrash, setShowCrash] = useState(false);
  const chapters = section.chapters.map((ch, ci) => ({ ch, ci }));
  const core = chapters.filter(({ ch }) => ch.counts !== false);
  const crash = chapters.filter(({ ch }) => ch.counts === false);

  const renderChapter = ({ ch, ci }) => (
    <div key={ch.id} data-testid={`chapter-${ch.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-6 h-6 rounded-md bg-[#141B4D] text-white text-xs font-bold flex items-center justify-center">{ci}</span>
        <h3 className="font-bold text-[#141B4D] text-lg">{ch.title}</h3>
        {ch.badge && (
          <span data-testid={`chapter-badge-${ch.id}`}
            className="px-2 py-0.5 rounded-full bg-[#FFD100] text-[#141B4D] text-[10px] font-bold uppercase tracking-wide">{ch.badge}</span>
        )}
      </div>
      {ch.notice && (
        <div data-testid={`chapter-notice-${ch.id}`}
          className="flex items-start gap-2 mb-3 rounded-lg bg-[#FDF4D6] border border-[#FFD100]/60 px-3 py-2">
          <AlertCircle className="w-4 h-4 text-[#B8860B] mt-0.5 shrink-0" />
          <p className="text-xs text-[#5c4a00] leading-relaxed">{ch.notice}</p>
        </div>
      )}
      {ch.book && (
        <a href={ch.book.url} target="_blank" rel="noreferrer" data-testid={`chapter-book-${ch.id}`}
          className="flex items-start gap-3 mb-3 bg-[#141B4D] text-white rounded-xl p-4 card-lift">
          <BookOpen className="w-5 h-5 text-[#FFD100] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm">
              <span className="font-bold text-[#FFD100] italic">{ch.book.title}</span>
              <span className="text-white/60"> · a book by {ch.book.author}, {ch.book.author_role}</span>
            </div>
            <p className="text-sm text-white/75 mt-1 leading-relaxed">{ch.book.note}</p>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#FFD100] mt-2">
              View the book on Amazon <ExternalLink className="w-3 h-3" />
            </span>
          </div>
        </a>
      )}
      <div className="space-y-3">
        {ch.lessons.map((l, li) => <LessonRow key={l.id} lesson={l} label={`${ci}.${li + 1}`} done={completedIds.has(l.id)} onOpen={onOpen} />)}
      </div>
    </div>
  );

  return (
    <div className="brand-fade max-w-3xl space-y-6">
      <Callout title={section.label}>{section.blurb}</Callout>
      {core.map(renderChapter)}

      {crash.length > 0 && (
        <div className="pt-4 border-t border-[#BDC5DB]/60" data-testid="crash-courses-section">
          <button type="button" onClick={() => setShowCrash((v) => !v)} data-testid="toggle-crash-courses"
            className="w-full flex items-center gap-3 text-left bg-[#F2E9DB]/60 hover:bg-[#F2E9DB] rounded-xl p-4 card-lift transition-colors">
            <span className="w-9 h-9 rounded-lg bg-[#141B4D] text-[#FFD100] flex items-center justify-center shrink-0">
              <Library className="w-5 h-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block font-bold text-[#141B4D]">Crash Courses & Appendix <span className="text-[#101820]/50 font-semibold">(optional)</span></span>
              <span className="block text-sm text-[#101820]/60 mt-0.5">Optional deep dives into each department, plus the quick-reference appendix. These don't count toward your completion, explore any that are relevant to your role.</span>
            </span>
            {showCrash ? <ChevronUp className="w-5 h-5 text-[#141B4D] shrink-0" /> : <ChevronDown className="w-5 h-5 text-[#141B4D] shrink-0" />}
          </button>
          {showCrash && <div className="space-y-6 mt-6">{crash.map(renderChapter)}</div>}
        </div>
      )}
    </div>
  );
}

function ResourceLibrary({ section, completedIds, onOpen }) {
  return (
    <div className="brand-fade space-y-6">
      <Callout title={section.label}>{section.blurb}</Callout>
      {section.pods.map((pod) => (
        <div key={pod.id}>
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-4 h-4 text-[#1D57A5]" />
            <h3 className="font-bold text-[#141B4D] text-lg">{pod.title}</h3>
          </div>
          <p className="text-sm text-[#101820]/60 mb-3 max-w-3xl">{pod.desc}</p>
          <div className="grid md:grid-cols-2 gap-3">
            {pod.lessons.map((l) => <LessonRow key={l.id} lesson={l} label="•" done={completedIds.has(l.id)} onOpen={onOpen} />)}
          </div>
        </div>
      ))}

      <div>
        <div className="section-band rounded-lg px-4 py-3 mb-3 max-w-md">
          <span className="font-bold">USPTO Tools Bank</span>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {section.uspto_tools.map((t) => (
            <a key={t.name} href={t.url} target="_blank" rel="noreferrer" data-testid={`uspto-tool-${t.name}`}
              className="bg-white rounded-xl p-4 shadow-sm border border-black/5 card-lift">
              <div className="font-bold text-[#141B4D] flex items-center gap-1.5">{t.name} <ExternalLink className="w-3.5 h-3.5 text-[#1D57A5]" /></div>
              <div className="text-xs text-[#101820]/55 mt-1">{t.desc}</div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

function LessonView({ lesson, onBack, onComplete, label, chapterTitle, prev, next, onNav, glossary }) {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "auto" }); setAnswers({}); setSubmitted(false); }, [lesson.id]);
  const quiz = lesson.quiz || [];
  const allAnswered = quiz.every((_, i) => answers[i] != null);
  const score = quiz.filter((q, i) => answers[i] === q.correct).length;
  const passed = quiz.length === 0 || score === quiz.length;

  const submit = async () => {
    setSubmitted(true);
    if (passed) {
      await api.post("/classroom/progress", { lesson_id: lesson.id, completed: true, quiz_score: score });
      toast.success("Lesson complete! Progress saved.");
      onComplete();
    }
  };

  return (
    <div className="p-8 md:p-10 max-w-3xl brand-fade">
      <button onClick={onBack} data-testid="lesson-back" className="flex items-center gap-1 text-sm text-[#1D57A5] mb-4 hover:underline"><ArrowLeft className="w-4 h-4" /> Back to catalog</button>
      {label && (
        <div className="flex items-center gap-2 mb-2" data-testid="lesson-number">
          <span className="inline-flex items-center justify-center rounded-md bg-[#141B4D] text-white text-xs font-bold px-2 py-1">Lesson {label}</span>
          {chapterTitle && <span className="text-xs font-semibold uppercase tracking-widest text-[#101820]/45">{chapterTitle}</span>}
        </div>
      )}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <h1 className="text-3xl font-bold text-[#141B4D]">{lesson.title}</h1>
        {lesson.freshness && <FreshnessBadge freshness={lesson.freshness} uspto={lesson.uspto_dependent} />}
      </div>
      <div className="text-sm text-[#101820]/50 mt-1">
        {lesson.duration}
        {lesson.last_reviewed && <> · last reviewed {lesson.last_reviewed}{lesson.reviewed_by ? ` by ${lesson.reviewed_by}` : ""}</>}
        {lesson.owner && <> · owner {lesson.owner.split("@")[0]}</>}
      </div>

      {lesson.uspto_dependent && (
        <div className="mt-4 rounded-lg bg-[#FBF3D0] border border-[#FFD100] p-3 flex items-start gap-2 text-sm text-[#8a6d00]">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <span><span className="font-bold">USPTO-dependent lesson.</span> Fees, forms and click-paths change — always confirm the current details at the linked USPTO source before relying on this.</span>
        </div>
      )}

      {lesson.video && (
        <div className="mt-5 rounded-xl overflow-hidden shadow-sm border border-black/5 aspect-video bg-black">
          <iframe title={lesson.title} src={lesson.video} className="w-full h-full" allowFullScreen />
        </div>
      )}

      {lesson.summary && <p className="text-[15px] text-[#101820] leading-relaxed mt-5">{glossaryize(lesson.summary, glossary)}</p>}
      {lesson.body && <p className="text-[15px] text-[#101820] leading-relaxed mt-5">{glossaryize(lesson.body, glossary)}</p>}

      {lesson.transcript && (lesson.video ? (
        <details className="mt-4 bg-white rounded-xl border border-black/5 shadow-sm" data-testid="lesson-transcript">
          <summary className="cursor-pointer select-none px-4 py-3 font-semibold text-[#141B4D] flex items-center gap-2"><FileText className="w-4 h-4 text-[#1D57A5]" /> Full transcript</summary>
          <div className="px-4 pb-4 text-sm text-[#101820]/75"><RichText text={lesson.transcript} /></div>
        </details>
      ) : (
        <div className="mt-5 bg-white rounded-xl border border-black/5 shadow-sm p-5" data-testid="lesson-transcript">
          <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-[#1D57A5]" /> Lesson notes</div>
          <div className="text-[15px] text-[#101820]/90"><RichText text={lesson.transcript} /></div>
        </div>
      ))}

      {lesson.steps?.length > 0 && (
        <div className="mt-4 bg-white rounded-xl p-5 shadow-sm border border-black/5">
          <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-2">Steps</div>
          <ol className="list-decimal list-inside text-sm text-[#101820]/85 space-y-1.5">
            {lesson.steps.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </div>
      )}

      {lesson.spectrum && <SpectrumInteractive spectrum={lesson.spectrum} />}

      {lesson.risk_spectrum?.length > 0 && (
        <div className="mt-4 bg-white rounded-xl p-5 shadow-sm border border-black/5" data-testid="risk-spectrum">
          <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-1">Risk Spectrum</div>
          <p className="text-sm text-[#101820]/70 mb-4">Our search reports use this weather-forecast risk assessment — a plain-English way to communicate the risk level to the client.</p>
          <div className="rounded-lg border border-[#E4E1D8] overflow-hidden">
            <div className="grid grid-cols-[minmax(150px,220px)_1fr] bg-[#141B4D] text-white text-xs font-bold uppercase tracking-wider">
              <div className="px-4 py-2.5">Risk</div>
              <div className="px-4 py-2.5 border-l border-white/15">Description</div>
            </div>
            {lesson.risk_spectrum.map((r, i) => (
              <div key={i} className={`grid grid-cols-[minmax(150px,220px)_1fr] text-sm ${i % 2 ? "bg-[#FAF7F0]" : "bg-white"}`}>
                <div className="px-4 py-3 flex items-center gap-2.5 font-bold text-[#141B4D] border-t border-[#EDEAE1]">
                  <span className="w-3.5 h-3.5 rounded-full shrink-0 ring-2 ring-black/5" style={{ background: r.color }} />
                  {r.level}
                </div>
                <div className="px-4 py-3 text-[#101820]/85 border-l border-t border-[#EDEAE1]">{r.desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {lesson.callout && <div className="mt-4"><Callout title="Key takeaway">{glossaryize(lesson.callout, glossary)}</Callout></div>}


      {lesson.links?.length > 0 && (
        <div className="mt-4">
          <div className="text-xs font-bold uppercase tracking-widest text-[#101820]/50 mb-2">Source of truth & tools</div>
          <div className="flex flex-wrap gap-2">
            {lesson.links.map((l, i) => {
              if (l.deprecated) {
                return (
                  <a key={i} href={l.url} target="_blank" rel="noreferrer" data-testid={`lesson-link-${i}`}
                    title="This document is outdated — do not use"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 py-2 border border-dashed border-[#DC2626] bg-[#FBE9E9] text-[#B91C1C] transition-all hover:bg-[#F7D9D9]">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span className="line-through opacity-80">{l.label}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wide bg-[#DC2626] text-white rounded px-1.5 py-0.5">Outdated — do not use</span>
                  </a>
                );
              }
              const s = LINK_KIND[l.kind] || LINK_KIND.default;
              return (
                <a key={i} href={l.url} target="_blank" rel="noreferrer" data-testid={`lesson-link-${i}`}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-lg px-3 py-2 border transition-all"
                  style={{ color: s.color, background: s.bg, borderColor: s.border }}>
                  <LinkIcon className="w-3.5 h-3.5" /> {l.label}
                  {l.kind && <span className="text-[10px] uppercase opacity-70">· {l.kind}</span>}
                </a>
              );
            })}
          </div>
        </div>
      )}

      {lesson.downloads?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {lesson.downloads.map((d) => (
            <a key={d.name} href={d.url} className="flex items-center gap-1.5 text-sm bg-white border border-[#BDC5DB] rounded-lg px-3 py-2 text-[#1D57A5] hover:border-[#1D57A5]">
              <Download className="w-4 h-4" /> {d.name}
            </a>
          ))}
        </div>
      )}

      {quiz.length > 0 && (
        <div className="mt-8">
          <div className="section-band rounded-lg px-4 py-3 mb-4"><span className="font-bold">Knowledge Check</span></div>
          <div className="space-y-5">
            {quiz.map((q, qi) => (
              <div key={qi} data-testid={`quiz-q-${qi}`} className="bg-white rounded-xl p-5 shadow-sm border border-black/5">
                <div className="font-semibold text-[#141B4D] mb-3">{qi + 1}. {q.prompt}</div>
                <div className="space-y-2">
                  {q.options.map((opt, oi) => {
                    const chosen = answers[qi] === oi;
                    const isCorrect = oi === q.correct;
                    let cls = "border-[#BDC5DB] hover:border-[#1D57A5]";
                    if (submitted && chosen && !isCorrect) cls = "border-[#B91C1C] bg-[#FBE0E0]";
                    if (submitted && isCorrect) cls = "border-[#006747] bg-[#E3F0EA]";
                    else if (chosen) cls = "border-[#1D57A5] bg-[#E1ECF7]";
                    return (
                      <button key={oi} data-testid={`quiz-${qi}-opt-${oi}`} disabled={submitted && passed}
                        onClick={() => setAnswers({ ...answers, [qi]: oi })}
                        className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm transition-all ${cls}`}>{opt}</button>
                    );
                  })}
                </div>
                {submitted && answers[qi] !== q.correct && (
                  <div className="mt-2 text-xs text-[#B91C1C] bg-[#FBE0E0] rounded-lg p-2">↩ {q.explain}</div>
                )}
              </div>
            ))}
          </div>

          {submitted && (
            <div className={`mt-4 rounded-xl p-4 font-semibold ${passed ? "bg-[#E3F0EA] text-[#006747]" : "bg-[#FBE0E0] text-[#B91C1C]"}`}>
              {passed ? `Perfect — ${score}/${quiz.length}. Lesson marked complete!` : `${score}/${quiz.length} correct. Review the highlighted answers and try again.`}
            </div>
          )}

          <Button data-testid="quiz-submit" disabled={!allAnswered || (submitted && passed)} onClick={submit}
            className="mt-4 bg-[#FFD100] text-[#101820] font-bold hover:brightness-95">
            {submitted && !passed ? "Re-check answers" : "Submit & complete"}
          </Button>
        </div>
      )}

      {quiz.length === 0 && (
        <Button data-testid="lesson-complete" onClick={submit} className="mt-6 bg-[#FFD100] text-[#101820] font-bold hover:brightness-95">Mark complete</Button>
      )}

      {(prev || next) && (
        <div className="mt-10 pt-6 border-t border-[#BDC5DB]/60 grid grid-cols-2 gap-3" data-testid="lesson-nav">
          {prev ? (
            <button data-testid="lesson-prev" onClick={() => onNav(prev.lesson)}
              className="group text-left bg-white rounded-xl p-4 shadow-sm border border-black/5 card-lift flex items-center gap-3">
              <ArrowLeft className="w-4 h-4 text-[#1D57A5] shrink-0" />
              <div className="min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-widest text-[#101820]/45">Previous · {prev.label}</div>
                <div className="font-semibold text-[#141B4D] truncate">{prev.lesson.title}</div>
              </div>
            </button>
          ) : <div />}
          {next ? (
            <button data-testid="lesson-next" onClick={() => onNav(next.lesson)}
              className="group text-right bg-white rounded-xl p-4 shadow-sm border border-black/5 card-lift flex items-center justify-end gap-3">
              <div className="min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-widest text-[#101820]/45">Next · {next.label}</div>
                <div className="font-semibold text-[#141B4D] truncate">{next.lesson.title}</div>
              </div>
              <ArrowLeft className="w-4 h-4 text-[#1D57A5] shrink-0 rotate-180" />
            </button>
          ) : <div />}
        </div>
      )}
    </div>
  );
}

function Ask() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const ask = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true); setRes(null);
    try { setRes((await api.post("/classroom/ask", { question: q })).data); }
    finally { setLoading(false); }
  };
  return (
    <div className="brand-fade max-w-2xl">
      <Callout title="Ask the Classroom">Natural-language search across lessons, SOPs, the FAQ Bank & glossary. Get the answer + its source — no re-watching videos.</Callout>
      <form onSubmit={ask} className="flex gap-2 mt-4">
        <Input data-testid="ask-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. Is maintenance at 5 years or 10?" className="h-11 bg-white border-[#BDC5DB]" />
        <Button data-testid="ask-btn" type="submit" disabled={loading} className="h-11 bg-[#FFD100] text-[#101820] font-bold hover:brightness-95">Ask</Button>
      </form>
      {loading && <div className="mt-4 text-[#141B4D] animate-pulse">Searching the knowledge layer…</div>}
      {res && (
        <div className="mt-4 bg-white rounded-xl p-5 shadow-sm border-l-4 border-[#1D57A5]" data-testid="ask-answer">
          <p className="text-[15px] text-[#101820] leading-relaxed">{res.answer}</p>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <span className="text-xs font-bold uppercase tracking-widest text-[#101820]/50">Sources:</span>
            {res.sources?.length ? res.sources.map((s, i) => <Pill key={i} tone="cobalt">{s}</Pill>) : <span className="text-xs text-[#101820]/40">—</span>}
            <Pill tone={res.ai ? "green" : "pale"} className="ml-auto">{res.ai ? "Claude Sonnet 4.6" : "Keyword match"}</Pill>
          </div>
        </div>
      )}
    </div>
  );
}

function ProgressView({ progress, mode = "trademark", courseTitle = "The Trademark Classroom", lessonIds }) {
  const { user } = useAuth();
  const diplomaRef = useRef(null);
  if (!progress) return null;
  const idSet = lessonIds && lessonIds.length ? new Set(lessonIds) : null;
  const mineList = progress.mine.filter((p) => p.completed && (!idSet || idSet.has(p.lesson_id)));
  const total = idSet ? idSet.size : progress.total;
  const mineDone = mineList.length;
  const pct = total ? Math.round((mineDone / total) * 100) : 0;
  const certified = total > 0 && mineDone >= total;
  const isTrademark = mode === "trademark";
  const year = new Date().getFullYear();
  const downloadDiploma = async () => {
    if (!diplomaRef.current) return;
    try {
      const canvas = await html2canvas(diplomaRef.current, { scale: 3, backgroundColor: "#141B4D", useCORS: true });
      const link = document.createElement("a");
      link.download = `Indie-University-${courseTitle.replace(/\s+/g, "-")}-${(user?.name || "Diploma").replace(/\s+/g, "-")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Diploma downloaded 🎓");
    } catch (e) {
      toast.error("Couldn't generate the diploma image.");
    }
  };
  return (
    <div className="brand-fade grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl p-6 shadow-sm border border-black/5">
        <h3 className="font-bold text-[#141B4D] text-lg mb-3">Your progress</h3>
        <div className="flex items-center gap-3 mb-2">
          <Progress value={pct} className="h-3 flex-1" />
          <span className="font-bold text-[#141B4D]">{pct}%</span>
        </div>
        <div className="text-sm text-[#101820]/60">{mineDone} of {total} lessons complete</div>

        {certified && isTrademark ? (
          <>
            <div ref={diplomaRef} data-testid="alumni-diploma"
              className="mt-5 rounded-xl p-6 text-center bg-[#141B4D] text-white border-4 border-[#FFD100] relative overflow-hidden">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFD100]/80">Indie University</div>
              <Award className="w-10 h-10 mx-auto text-[#FFD100] my-2" />
              <div className="font-varsity text-2xl text-white leading-none">Alumni</div>
              <div className="text-xs text-white/70 mt-2">This certifies that</div>
              <div className="font-varsity text-lg text-[#FFD100] mt-1">{user?.name}</div>
              <div className="text-xs text-white/70 mt-2">has completed every chapter of The Trademark Classroom.</div>
              <div className="mt-3 inline-block text-[11px] font-bold uppercase tracking-widest bg-[#FFD100] text-[#141B4D] rounded-full px-3 py-1">Class of {year}</div>
            </div>
            <Button data-testid="download-diploma-btn" onClick={downloadDiploma}
              className="w-full mt-3 h-11 bg-[#FFD100] text-[#101820] font-bold hover:brightness-95">
              <Download className="w-4 h-4 mr-2" /> Download my diploma
            </Button>
          </>
        ) : certified ? (
          <>
            <div ref={diplomaRef} data-testid="orientation-diploma"
              className="mt-5 rounded-xl p-6 text-center bg-[#141B4D] text-white border-4 border-[#FFD100] relative overflow-hidden">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFD100]/80">Indie University</div>
              <GraduationCap className="w-10 h-10 mx-auto text-[#FFD100] my-2" />
              <div className="font-varsity text-2xl text-white leading-none">Oriented!</div>
              <div className="text-xs text-white/70 mt-2">This certifies that</div>
              <div className="font-varsity text-lg text-[#FFD100] mt-1">{user?.name}</div>
              <div className="text-xs text-white/70 mt-2">has completed New Teammate Orientation.</div>
              <div className="mt-3 inline-block text-[11px] font-bold uppercase tracking-widest bg-[#FFD100] text-[#141B4D] rounded-full px-3 py-1">Class of {year}</div>
            </div>
            <Button data-testid="download-diploma-btn" onClick={downloadDiploma}
              className="w-full mt-3 h-11 bg-[#FFD100] text-[#101820] font-bold hover:brightness-95">
              <Download className="w-4 h-4 mr-2" /> Download my certificate
            </Button>
          </>
        ) : (
          <div data-testid="student-status" className="mt-5 rounded-xl p-5 text-center bg-[#F2E9DB] border border-[#BDC5DB]">
            <GraduationCap className="w-10 h-10 mx-auto text-[#BDC5DB]" />
            <div className="font-bold mt-2 text-[#141B4D]">
              {isTrademark ? `Enrolled Student · Class of ${year}` : "In Progress"}
            </div>
            <div className="text-xs mt-1 text-[#101820]/50">
              {isTrademark
                ? "Complete every lesson to graduate and earn your Indie University Alumni diploma."
                : `Work through every lesson to finish ${courseTitle}.`}
            </div>
          </div>
        )}
      </div>

      {isTrademark && (
      <div className="bg-white rounded-xl p-6 shadow-sm border border-black/5">
        <h3 className="font-bold text-[#141B4D] text-lg mb-3">Team progress</h3>
        <div className="space-y-3">
          {progress.team.length === 0 && <div className="text-sm text-[#101820]/40">No team activity yet.</div>}
          {progress.team.map((t) => {
            const p = Math.round((t.completed / t.total) * 100);
            return (
              <div key={t.user} className="flex items-center gap-3">
                <Avatar name={t.name} size={32} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#141B4D] truncate">{t.name}</div>
                  <Progress value={p} className="h-2 mt-1" />
                </div>
                <span className="text-xs text-[#101820]/50 w-10 text-right">{p}%</span>
              </div>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
}
